import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import type { HookAPI } from 'antd/es/modal/useModal'
import { isAxiosError } from 'axios'
import { useState } from 'react'
import { toast } from 'sonner'

import AtomModal from '@/components/Atoms/AtomModal/AtomModal'
import { SortablePriorityRow } from '@/components/PrioritySortShared/PrioritySortRow/PrioritySortRow'
import { useReorderSegmentsMutation } from '@/hooks/segments/useReorderSegmentsMutation'
import type { SegmentModel } from '@/types'
import { getApiErrorData } from '@/utils/api/errors/errorGuards'
import { handleApiError } from '@/utils/api/errors/handleApiError'

import { getSegmentStatusLabel } from '../SegmentsTab.config'
import cn from './SegmentsOrderModal.module.scss'

const CONFLICT_STATUS = 409

type SegmentsOrderModalProps = {
  open: boolean
  segments: SegmentModel[]
  modal: HookAPI
  onClose: () => void
  onReload: () => Promise<SegmentModel[] | undefined>
}

const toIds = (segments: SegmentModel[]) => segments.map(({ id }) => id)

function SegmentsOrderModal({
  open,
  segments,
  modal,
  onClose,
  onReload,
}: SegmentsOrderModalProps) {
  const [orderedIds, setOrderedIds] = useState(() => toIds(segments))
  const reorder = useReorderSegmentsMutation()
  const segmentsById = new Map(segments.map((segment) => [segment.id, segment]))
  const isChanged = orderedIds.some((id, index) => segments[index]?.id !== id)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    const oldIndex = orderedIds.indexOf(String(active.id))
    const newIndex = orderedIds.indexOf(String(over.id))
    if (oldIndex === -1 || newIndex === -1) return
    setOrderedIds(arrayMove(orderedIds, oldIndex, newIndex))
  }

  const handleSave = () => {
    if (!isChanged) {
      onClose()
      return
    }
    reorder.mutate(orderedIds, {
      onSuccess: () => {
        toast.success('Порядок сегментов сохранён')
        onClose()
      },
      onError: async (error) => {
        if (isAxiosError(error) && error.response?.status === CONFLICT_STATUS) {
          toast.error(
            getApiErrorData(error)?.message ?? 'Список сегментов изменился'
          )
          const reloaded = await onReload()
          if (reloaded) setOrderedIds(toIds(reloaded))
          return
        }
        handleApiError(error, modal)
      },
    })
  }

  return (
    <AtomModal
      open={open}
      type="confirm"
      title="Порядок сегментов"
      okText="Сохранить"
      width={560}
      confirmLoading={reorder.isPending}
      onOk={handleSave}
      onCancel={onClose}
    >
      <p className={cn.hint}>
        Перетащите сегменты: сверху — самый дешёвый, снизу — самый дорогой.
      </p>
      <div className={cn.list}>
        <DndContext
          sensors={sensors}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={orderedIds}
            strategy={verticalListSortingStrategy}
          >
            {orderedIds.map((id, index) => {
              const segment = segmentsById.get(id)
              if (!segment) return null
              return (
                <SortablePriorityRow
                  key={id}
                  id={id}
                  index={index}
                  name={segment.description}
                  code={segment.code}
                  isChanged={segments[index]?.id !== id}
                  extra={
                    <span className={cn.extra}>
                      {segment.isRrc && <span className={cn.badge}>РРЦ</span>}
                      {segment.status !== 'AVAILABLE' && (
                        <span className={cn.status}>
                          {getSegmentStatusLabel(segment.status)}
                        </span>
                      )}
                      <span className={cn.markup}>
                        наценка {segment.markupPercentage}%
                      </span>
                    </span>
                  }
                />
              )
            })}
          </SortableContext>
        </DndContext>
      </div>
    </AtomModal>
  )
}

export default SegmentsOrderModal
